import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download,
  Filter,
  Loader2,
  MapPin,
  Package,
  RefreshCw,
  X,
  FileSpreadsheet,
  FileText,
  Columns3,
  Check,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Oct 2026 — Stock Position Report (web + mobile).
 *
 * Filters a flat snapshot of warehouse_placements by alphabetical
 * location range (A–F etc.) and / or division. Each row shows location,
 * pallet, product, SKU, qty, division, warehouse. Both filters are
 * independent and can be combined.
 *
 * Export dialog lets the admin pick a subset of columns and choose
 * Excel (XLSX) or PDF.
 */

export default function AdminStockPosition() {
  const token = localStorage.getItem('mr_admin_token') || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [locFrom, setLocFrom] = useState('');
  const [locTo, setLocTo] = useState('');
  const [division, setDivision] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [exportCols, setExportCols] = useState({});
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (locFrom) params.location_from = locFrom;
      if (locTo) params.location_to = locTo;
      if (division) params.division = division;
      if (warehouseId) params.warehouse_id = warehouseId;
      const { data } = await axios.get(`${API}/admin/stock-position-report`, {
        params, headers: authHeaders,
      });
      setReport(data);
      if (!Object.keys(exportCols).length) {
        const init = {};
        (data.columns || []).forEach((c) => { init[c.key] = true; });
        setExportCols(init);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Report failed to load.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders, locFrom, locTo, division, warehouseId]);

  useEffect(() => { load(); /* initial */ }, [load]);

  const resetFilters = () => {
    setLocFrom(''); setLocTo(''); setDivision(''); setWarehouseId(''); setSearch('');
  };

  const filteredRows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    if (!q) return report.rows;
    return report.rows.filter((r) => (
      (r.sku || '').toLowerCase().includes(q)
      || (r.product_name || '').toLowerCase().includes(q)
      || (r.location_id || '').toLowerCase().includes(q)
      || (r.pallet_id || '').toLowerCase().includes(q)
    ));
  }, [report, search]);

  const totalQty = useMemo(
    () => filteredRows.reduce((s, r) => s + (r.qty || 0), 0),
    [filteredRows],
  );
  const palletCount = useMemo(() => {
    const s = new Set();
    for (const r of filteredRows) {
      if (r.pallet_id && r.pallet_id !== '—') s.add(r.pallet_id);
    }
    return s.size;
  }, [filteredRows]);

  const openExport = () => {
    if (!report) {
      toast.message('Run the report first.');
      return;
    }
    setShowExport(true);
  };

  const toggleExportCol = (key) => {
    setExportCols((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const doExport = useCallback(async (fmt /* "xlsx" | "pdf" */) => {
    if (!report) return;
    const selectedKeys = (report.columns || [])
      .map((c) => c.key).filter((k) => exportCols[k]);
    if (!selectedKeys.length) {
      toast.warning('Pick at least one column.');
      return;
    }
    setExporting(true);
    try {
      const params = {
        columns: selectedKeys.join(','),
      };
      if (locFrom) params.location_from = locFrom;
      if (locTo) params.location_to = locTo;
      if (division) params.division = division;
      if (warehouseId) params.warehouse_id = warehouseId;
      const url = `${API}/admin/stock-position-report/export.${fmt}`;
      const res = await axios.get(url, {
        params, headers: authHeaders,
        responseType: 'blob',
      });
      const mime = fmt === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const blob = new Blob([res.data], { type: mime });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `stock_position_${stamp}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(`${fmt.toUpperCase()} downloaded`);
      setShowExport(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || `${fmt.toUpperCase()} export failed.`);
    } finally {
      setExporting(false);
    }
  }, [authHeaders, exportCols, report, locFrom, locTo, division, warehouseId]);

  // ---- render -----------------------------------------------------------

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="stock-position-page">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-neutral-900">
              Stock Position
            </h1>
            <p className="text-xs sm:text-sm text-neutral-600 mt-0.5 max-w-2xl">
              Filter current pallet positions by location range (A–Z) and / or division.
              Both filters work independently and together.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="refresh-btn"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 bg-white hover:bg-neutral-50 border border-neutral-300 text-neutral-800 text-xs sm:text-sm font-semibold px-3 py-2 rounded-lg shadow-sm disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              type="button"
              data-testid="export-open-btn"
              onClick={openExport}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold px-3 py-2 rounded-lg shadow-sm"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </header>

        {/* --- Filters --- */}
        <section
          data-testid="filters-panel"
          className="bg-white border border-neutral-200 rounded-xl shadow-sm"
        >
          <button
            type="button"
            data-testid="toggle-filters-btn"
            onClick={() => setShowFilters((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left sm:hidden border-b border-neutral-200"
          >
            <span className="font-semibold text-sm text-neutral-900 inline-flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </span>
            <span className="text-xs text-neutral-500">{showFilters ? 'Hide' : 'Show'}</span>
          </button>
          <div className={`${showFilters ? '' : 'hidden'} sm:block p-4 space-y-3`}>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                  Location from
                </label>
                <select
                  data-testid="loc-from"
                  value={locFrom}
                  onChange={(e) => setLocFrom(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  <option value="">Any</option>
                  {ALPHABET.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                  Location to
                </label>
                <select
                  data-testid="loc-to"
                  value={locTo}
                  onChange={(e) => setLocTo(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  <option value="">Any</option>
                  {ALPHABET.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                  Division
                </label>
                <select
                  data-testid="division-filter"
                  value={division}
                  onChange={(e) => setDivision(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  <option value="">All divisions</option>
                  {(report?.divisions || []).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                  Warehouse
                </label>
                <select
                  data-testid="warehouse-filter"
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                >
                  <option value="">All warehouses</option>
                  {(report?.warehouses || []).map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
              <input
                type="text"
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search SKU, name, location, pallet…"
                className="w-full sm:w-80 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              <button
                type="button"
                data-testid="reset-filters-btn"
                onClick={resetFilters}
                className="text-xs font-semibold text-neutral-700 hover:text-neutral-900 border border-neutral-300 bg-white hover:bg-neutral-50 px-3 py-1.5 rounded-lg"
              >
                Reset filters
              </button>
            </div>
          </div>
        </section>

        {/* --- Summary cards --- */}
        <section data-testid="summary-panel" className="grid grid-cols-3 gap-2 sm:gap-3">
          <SummaryCard label="Rows" value={filteredRows.length} icon={MapPin} />
          <SummaryCard label="Pallets" value={palletCount} icon={Package} />
          <SummaryCard label="Total qty" value={totalQty} icon={Columns3} />
        </section>

        {/* --- Mobile card list --- */}
        <section className="sm:hidden space-y-2" data-testid="rows-mobile">
          {loading && (
            <div className="text-center py-6 text-neutral-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
            </div>
          )}
          {!loading && filteredRows.length === 0 && (
            <div className="text-center py-6 text-neutral-500 text-sm">No rows for the current filters.</div>
          )}
          {!loading && filteredRows.map((r, idx) => (
            <div
              key={`${r.pallet_id}-${r.product_id}-${idx}`}
              data-testid={`row-mobile-${idx}`}
              className="bg-white border border-neutral-200 rounded-lg p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-mono font-bold text-neutral-900 text-sm">{r.location_id}</div>
                <div className="text-xs font-mono text-neutral-500">{r.pallet_id}</div>
              </div>
              <div className="text-sm text-neutral-900 mt-1 font-semibold line-clamp-1">
                {r.product_name}
              </div>
              <div className="text-[11px] text-neutral-500 mt-0.5">{r.sku}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="inline-block text-[10px] font-semibold uppercase tracking-wider bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded-full">
                  {r.division}
                </span>
                <span className="font-mono font-bold text-emerald-700 tabular-nums">
                  {r.qty}
                </span>
              </div>
              {(r.stage || r.hold_pallet || r.shipped || r.dispatch_locked) && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {r.stage && <Badge tone="amber">Staged</Badge>}
                  {r.hold_pallet && <Badge tone="rose">On hold</Badge>}
                  {r.dispatch_locked && <Badge tone="indigo">Dispatch locked</Badge>}
                  {r.shipped && <Badge tone="neutral">Shipped</Badge>}
                </div>
              )}
            </div>
          ))}
        </section>

        {/* --- Desktop table --- */}
        <section
          className="hidden sm:block bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden"
          data-testid="rows-desktop"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Warehouse</th>
                  <th className="px-3 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-left">Pallet</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-left">Division</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-neutral-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
                  </td></tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-neutral-500">
                    No rows for the current filters.
                  </td></tr>
                )}
                {!loading && filteredRows.map((r, idx) => (
                  <tr
                    key={`${r.pallet_id}-${r.product_id}-${idx}`}
                    data-testid={`row-desktop-${idx}`}
                    className="border-t border-neutral-100 hover:bg-neutral-50"
                  >
                    <td className="px-3 py-1.5">{r.warehouse_name}</td>
                    <td className="px-3 py-1.5 font-mono font-semibold">{r.location_id}</td>
                    <td className="px-3 py-1.5 font-mono text-neutral-700">{r.pallet_id}</td>
                    <td className="px-3 py-1.5">{r.product_name}</td>
                    <td className="px-3 py-1.5 font-mono text-neutral-600">{r.sku}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold tabular-nums">{r.qty}</td>
                    <td className="px-3 py-1.5">
                      <span className="inline-block text-[10px] font-semibold uppercase tracking-wider bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded-full">
                        {r.division}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {r.stage && <Badge tone="amber">Staged</Badge>}
                        {r.hold_pallet && <Badge tone="rose">On hold</Badge>}
                        {r.dispatch_locked && <Badge tone="indigo">Dispatch locked</Badge>}
                        {r.shipped && <Badge tone="neutral">Shipped</Badge>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* --- Export dialog --- */}
        {showExport && report && (
          <div
            className="fixed inset-0 z-50 bg-neutral-900/60 flex items-end sm:items-center justify-center p-4"
            data-testid="export-dialog"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-neutral-900">Export report</h2>
                  <p className="text-xs text-neutral-600 mt-0.5">
                    Pick which columns to include. {filteredRows.length} row{filteredRows.length === 1 ? '' : 's'} will be exported with the current filters.
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="export-close-btn"
                  onClick={() => setShowExport(false)}
                  className="text-neutral-500 hover:text-neutral-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(report.columns || []).map((c) => {
                  const on = !!exportCols[c.key];
                  return (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`export-col-${c.key}`}
                      onClick={() => toggleExportCol(c.key)}
                      className={`text-left px-3 py-2 rounded-lg border text-sm transition flex items-center gap-2 ${
                        on
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                          : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-400'
                      }`}
                    >
                      <span className={`inline-flex items-center justify-center w-4 h-4 rounded border ${on ? 'bg-emerald-600 border-emerald-700' : 'border-neutral-300'}`}>
                        {on && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className="font-semibold">{c.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-200">
                <button
                  type="button"
                  data-testid="export-xlsx-btn"
                  onClick={() => doExport('xlsx')}
                  disabled={exporting}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2 rounded-lg shadow-sm disabled:opacity-50"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
                <button
                  type="button"
                  data-testid="export-pdf-btn"
                  onClick={() => doExport('pdf')}
                  disabled={exporting}
                  className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm px-4 py-2 rounded-lg shadow-sm disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl px-3 py-2 sm:px-4 sm:py-3 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold tracking-wider text-neutral-500 uppercase">{label}</span>
        <Icon className="w-4 h-4 text-neutral-400" />
      </div>
      <div className="text-xl sm:text-2xl font-black mt-0.5 tabular-nums text-neutral-900">{value}</div>
    </div>
  );
}

function Badge({ tone = 'neutral', children }) {
  const cls = {
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    rose: 'bg-rose-100 text-rose-800 border-rose-200',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    neutral: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  }[tone];
  return (
    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {children}
    </span>
  );
}
